/**
 * Les refus du moteur, en français.
 *
 * `resolve()` lève `InvalidAction` avec un **code fermé** (SPEC-MOTEUR §1.8) et des `details`.
 * Le mapping est exhaustif par construction : `Record<InvalidActionCode, …>` ne compile plus
 * si le moteur ajoute un code — c'est le filet.
 */

import { InvalidAction, PALIER_GRAND_SAUT, type InvalidActionCode } from "@maxime/shared";

import { nombre } from "./format";

type Details = Record<string, number | string | boolean | null>;

const MESSAGES: Record<InvalidActionCode, (d: Details) => string> = {
  not_enough_energy: (d) =>
    `Plus d'énergie (${nombre(Number(d.disponible ?? 0))} ⚡ sur ${nombre(Number(d.requis ?? 0))} nécessaires). Va dormir.`,
  not_enough_money: (d) =>
    `Pas assez de Clicks : il en faut ${nombre(Number(d.requis ?? 0))}, tu en as ${nombre(Number(d.disponible ?? 0))}.`,
  not_enough_pc: () => "Pas assez de Points de Créativité pour ce nœud.",
  not_enough_cards: () => "Il te manque des idées en main. Fais de la veille.",
  format_locked: (d) =>
    `Les vidéos longues s'ouvrent à ${nombre(Number(d.requis ?? 100))} abonnés. Tu en as ${nombre(Number(d.subscribers ?? 0))} — encore un peu de shorts.`,
  subject_not_in_hand: () => "Ce sujet n'est pas dans ta main.",
  theme_exhausted: () => "Tu as déjà trop tapé sur ce thème : le public sature. Change de sujet.",
  skill_unknown: () => "Ce nœud d'arbre n'existe pas.",
  skill_already_owned: () => "Tu as déjà ce nœud.",
  skill_prereq_missing: () => "Il te manque le nœud précédent de la branche.",
  respec_locked: () => "La remise à zéro de l'arbre s'ouvre à 10 000 abonnés.",
  video_not_found: () => "Cette vidéo n'existe pas.",
  video_wrong_class: () => "Cette action ne s'applique pas à ce type de vidéo.",
  series_too_small: () => "Il faut au moins 3 vidéos pour faire une série.",
  jalon_conditions_not_met: () => "Les conditions de ce jalon ne sont pas réunies.",
  jalon_already_done: () => "Ce jalon est déjà fait.",
  event_choice_invalid: () => "Ce choix n'est pas disponible.",
  no_pending_event: () => "Aucun événement en attente.",
  pending_event_blocks: () => "Règle d'abord l'événement en cours : le reste attend.",
  run_not_active: () => "Cette run est terminée.",
  score_implausible: () => "Score refusé : trop beau pour être vrai.",
  delegation_locked: () => "Tu ne peux pas encore déléguer cette étape.",
  // ⟦SPEC-PRODUCTEUR §1.5 / §4.5⟧ Le droit existe, la caisse ne suit pas — la règle des
  // ciseaux : les cinq étapes se paient, ou la vidéo ne se fait pas.
  delegation_impayable: (d) =>
    `L'équipe coûte ${nombre(Number(d.total ?? 0))} ₵ pour cette vidéo, et tu en as ${nombre(Number(d.solde ?? 0))}. Tu paies tout le monde, ou tu reprends les ciseaux.`,
  pause_non_decouverte: (d) =>
    d.deja_en_pause === true ? "Tu es déjà en pause annoncée." : "Tu ne connais pas encore cette possibilité.",
  // ⟦A-11 §2⟧ Les deux sens du déménagement, refusés par la même règle.
  logement_invalide: (d) => {
    if (d.raison === "pas_encore_parti") return "Tu vis encore chez tes parents — il n'y a rien à quitter.";
    if (d.raison === "deja_chez_les_parents") return "Tu es déjà rentré chez tes parents.";
    return "Tu as ton studio : il n'y a rien à reprendre.";
  },
  // ⟦A-11 §3⟧ On refuse au lieu de plafonner : débiter des Clicks qui n'achètent rien serait pire.
  production_value_invalide: (d) => {
    if (d.raison === "palier")
      return `La production value s'ouvre à ${nombre(Number(d.requis ?? 0))} abonnés. Tu en as ${nombre(Number(d.subscribers ?? 0))}.`;
    if (d.raison === "plafond")
      return `Au-delà de ${nombre(Number(d.max ?? 0))} Clicks, tu paies pour rien : c'est déjà le plein tarif.`;
    return "Une production value négative, ça n'existe pas.";
  },
  objet_inconnu: () => "Cet objet n'existe pas dans la boutique.",
  objet_deja_possede: () => "Tu as déjà cet objet — on n'achète pas deux fois un meuble.",
  objet_prereq_manquant: (d) =>
    d.premier_manquant === null
      ? "Il te manque un objet prérequis."
      : `Il te faut d'abord : ${String(d.premier_manquant)}.`,
  objet_verrouille: (d) =>
    `Cet objet s'ouvre à ${nombre(Number(d.palier_min ?? 0))} abonnés. Tu en as ${nombre(Number(d.subscribers ?? 0))}.`,
  // ⟦A-60⟧ Un seul matos armé à la fois — ce n'est pas une limite de sac, c'est la règle qui
  // garde les consommables à leur place : un coup de pouce, jamais un second arbre.
  objet_deja_prepare: () =>
    "Tu as déjà du matos prêt pour la prochaine vidéo. Une seule aide à la fois — sers-t'en d'abord.",
  /**
   * ⟦A-5⟧ Le joueur ne devrait jamais voir ce message : l'UI n'affiche pas une mécanique
   * fermée. S'il tombe dessus, c'est un appel direct — on lui répond quand même en français.
   *
   * ⚠️ Le moteur pose `mecanique` pour **chacun** des sept étages, pas seulement les deux
   * premiers : `arbre`, `image`, `tendances`, `longue`… La formulation par défaut annonçait
   * pourtant des commentaires vides, quelle que soit la mécanique — un joueur qui touchait
   * l'arbre trop tôt lisait une phrase sur son téléphone. Depuis que les refus traversent
   * aussi l'API (`details.code`), ce chemin est bien plus fréquent : chaque étage a désormais
   * sa phrase, et le défaut ne parle que de ce qu'il sait.
   */
  mecanique_verrouillee: (d) => {
    // ⟦C60 · lot b · A-177⟧ Une mécanique que le grand ménage a COUPÉE (`details.coupe`) n'est pas « pas
    // encore ouverte » : elle n'existe plus. Sans cette ligne, la phrase par défaut annoncerait
    // « Encore 0 publication ». Le joueur ne devrait jamais la lire (aucun écran ne la propose).
    if (d.coupe === true) return "Ça n'existe pas dans ce jeu.";
    const manque = Number(d.publications_manquantes ?? 0);
    const reste = `Encore ${manque} publication${manque > 1 ? "s" : ""}.`;
    switch (d.mecanique) {
      case "veille":
        return "Ça n'existe pas encore. Publie d'abord quelque chose — on regarde ce que font les autres après avoir commencé, pas avant.";
      case "lire_commentaires":
        return `Tes commentaires sont encore vides. ${reste} Il y aura alors de quoi lire.`;
      case "image":
        return `Ta chaîne n'a pas encore de ligne : il faut publier pour qu'elle se dessine. ${reste}`;
      case "tendances":
        return `Tu ne regardes pas encore ce qui marche ailleurs. ${reste}`;
      case "arbre":
        return `Le panneau de liège n'est pas là : tes Points de Créativité n'ont pas encore où aller. ${reste}`;
      case "longue":
        return `Les vidéos longues arrivent plus tard. ${reste}`;
      // ⟦A-29⟧ Le seul étage qui ne se compte pas en publications : il s'ouvre au Grand Saut
      // ⟦A-1⟧. La phrase ne doit donc surtout pas emprunter `reste` — elle annoncerait
      // « encore 0 publication ». Le palier est LU dans le moteur, jamais recopié.
      //
      // ⚠️ Elle sert **deux** portes — une action payante (`validateAction`) et un nœud
      // d'arbre payant (`refusAchat`) — donc elle ne peut renvoyer vers aucune des deux en
      // particulier. Une première version finissait par « c'est le lit ou la pause » : juste
      // pour la thérapie, absurde sur une ring light.
      case "argent":
        return `Ça coûte des Clicks, et tu n'en as pas encore : la couche argent s'ouvre au Grand Saut, à ${nombre(PALIER_GRAND_SAUT)} abonnés.`;
      default:
        return `Cette mécanique n'est pas encore ouverte. ${reste}`;
    }
  },

  // — ⟦SPEC-FIL §1.4⟧ Les trois refus du Fil —
  //
  // ⚠️ Le joueur ne devrait **jamais** les lire : l'écran du Fil ne propose que les réponses
  // autorisées, sur les commentaires présents, dans la limite du lot. Ces phrases couvrent le
  // décalage — un onglet resté ouvert pendant qu'une nuit a fait expirer les commentaires, ou
  // un appel direct à l'API. Elles disent donc quoi FAIRE (rouvrir le Fil), pas ce qui a raté.
  fil_comment_unknown: (d) =>
    d.raison === "double"
      ? "Tu as répondu deux fois au même commentaire. Rouvre le Fil, il s'est remis à jour."
      : "Ce commentaire n'est plus dans ton Fil — il a défilé pendant que tu réfléchissais. Rouvre-le.",
  fil_lot_depasse: (d) =>
    `Une session de commentaires, c'est ${nombre(Number(d.lot ?? 3))} réponses. Le reste attendra la prochaine.`,
  fil_reponse_interdite: (d) =>
    d.raison === "deja_epingle"
      ? "Ce commentaire est déjà épinglé. On n'épingle pas deux fois la même chose."
      : "Tu ne peux pas répondre ça à ce commentaire-là.",
  // ⟦SPEC-PRODUCTEUR §1.5 / A-66⟧ Les deux refus de l'escalier de délégation.
  producteur_absent: (d) =>
    d.possede === true
      ? "Tu as congédié ton producteur — ou il est parti faute d'être payé. Repasse par l'équipe pour le rappeler."
      : "Personne ne décide à ta place pour l'instant. Il te faut Le producteur, dans la branche Business.",
  skill_locked_subs: (d) =>
    `Ce nœud attend ${Number(d.requis ?? 0).toLocaleString("fr-FR")} abonnés. Tu en as ${Number(d.disponible ?? 0).toLocaleString("fr-FR")}.`,
  // ⟦C8 · A-120⟧ « Le manager tranche » sans manager en poste.
  manager_absent: (d) =>
    d.possede === true
      ? "Ton manager n'est pas en poste — congédié, ou parti faute de paie. Repasse par l'équipe pour le rappeler."
      : "Il te faut Le manager (branche Business) avant que quelqu'un puisse trancher à ta place.",
  // ⟦C8 · A-120⟧ `regler_equipe` a demandé un candidat que le vivier ne connaît pas.
  candidat_inconnu: () => "Cette personne n'est plus au marché. Choisis-en une autre.",
};

/** Message français d'une erreur — `InvalidAction` typée, ou n'importe quoi d'autre. */
export function messageErreur(err: unknown): string {
  if (err instanceof InvalidAction) return MESSAGES[err.code](err.details);
  if (err instanceof Error) return err.message;
  return "Quelque chose a cassé. Recharge la page.";
}
